"use client";

import { Eye, Feather, Flame, Grid3x3, Search, Waves } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  number: "01",
  title: "Habitat Assessment",
  description: "We begin by observing the land. Our team documents the current state of the refuge, identifying critical nesting sites and potential environmental threats.",
  detail: "TYPICAL DURATION: 2-4 DAYS / DELIVERABLE: SITE SURVEY REPORT",
  icon: Feather
}, {
  number: "02",
  title: "Species Audit",
  description: "Each bird population is monitored against seasonal migration patterns and food availability. We prioritize the most vulnerable species for immediate action.",
  detail: "TYPICAL DURATION: 1-2 DAYS / DELIVERABLE: WILDLIFE CENSUS",
  icon: Search
}, {
  number: "03",
  title: "Protection Strategy",
  description: "Conservation plans are drafted with precision. We create buffer zones and educational signage that protect wildlife while allowing for public appreciation.",
  detail: "TYPICAL DURATION: 3-5 DAYS / DELIVERABLE: MANAGEMENT PLAN",
  icon: Grid3x3
}, {
  number: "04",
  title: "Community Review",
  description: "We invite local stakeholders to review our findings. Transparency ensures that our advocacy efforts are supported by the community we serve.",
  detail: "TYPICAL DURATION: 1-2 DAYS / DELIVERABLE: FEEDBACK SUMMARY",
  icon: Eye
}, {
  number: "05",
  title: "Implementation",
  description: "From installing protective fencing to restoring native vegetation, we execute our plans with the care required to preserve delicate ecosystems.",
  detail: "TYPICAL DURATION: 4-7 DAYS / DELIVERABLE: COMPLETED RESTORATION",
  icon: Flame
}, {
  number: "06",
  title: "Impact Verification",
  description: "We return to the field to measure success. We ensure that our interventions have effectively shielded the wildlife and sea birds from human disturbance.",
  detail: "TYPICAL DURATION: 2-3 DAYS / DELIVERABLE: IMPACT REPORT",
  icon: Waves
}];
function StageBlock({
  stage,
  index
}: {
  stage: (typeof stages)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const isLeft = index % 2 === 0;
  const Icon = stage.icon;
  return <div data-index={index} ref={ref} className="relative grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 items-start">
      <div className="absolute left-[-6px] md:left-[calc(0%-6px)] top-3 w-3 h-3 rounded-lg bg-blue-600 z-10" />

      <div className={`pl-8 md:pl-0 ${isLeft ? "md:col-start-1 md:pr-12" : "md:col-start-2 md:pl-12"}`}>
        <div className="overflow-hidden" style={{
        transition: `clip-path 900ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 40}ms, opacity 900ms ease ${index * 40}ms`,
        clipPath: isVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
        opacity: isVisible ? 1 : 0
      }}>
          <div className="flex items-baseline gap-4 mb-6">
            <span className="font-default text-5xl md:text-6xl text-slate-900 relative inline-block" style={{
            fontWeight: 500
          }}>
              {stage.number}
              <span className="absolute left-0 right-0 top-1/2 h-px bg-slate-900/40" />
            </span>
            <span className="font-default text-xs tracking-widest text-slate-600 uppercase" style={{
            letterSpacing: "0.18em"
          }}>
              Phase
            </span>
          </div>

          <div className="flex items-start gap-5 mb-6">
            <div className="shrink-0 w-14 h-14 rounded-lg bg-green-100 flex items-center justify-center text-green-700" aria-hidden="true">
              <Icon className="w-7 h-7" strokeWidth={1.25} />
            </div>
            <h3 className="font-default text-3xl md:text-4xl text-slate-900 leading-tight" style={{
            fontWeight: 600
          }}>
              {stage.title}
            </h3>
          </div>

          <p className="font-default text-lg leading-relaxed text-slate-700 max-w-[420px]" style={{
          fontWeight: 400
        }}>
            {stage.description}
          </p>

          <p className="mt-8 font-default text-xs text-slate-500 uppercase max-w-[420px]" style={{
          letterSpacing: "0.14em",
          lineHeight: 1.7
        }}>
            {stage.detail}
          </p>
        </div>
      </div>
    </div>;
}
function PullQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.3
  });
  return <div ref={ref} className="relative pl-8 md:pl-0 my-12 md:my-20">
      <div className="absolute left-[-6px] md:left-[calc(0%-6px)] top-6 w-3 h-3 rounded-lg bg-green-800 z-10" />
      <div className="md:px-16 max-w-3xl mx-auto text-center overflow-hidden" style={{
      transition: "clip-path 1100ms cubic-bezier(0.4, 0, 0.2, 1), opacity 1100ms ease",
      clipPath: isVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
      opacity: isVisible ? 1 : 0
    }}>
        <p className="font-default italic text-3xl md:text-5xl leading-[1.2] text-green-800" style={{
        fontWeight: 400
      }}>
          &ldquo;Protection is not a single act, but a continuous commitment to the wild.&rdquo;
        </p>
        <p className="mt-8 font-default text-xs uppercase text-slate-600" style={{
        letterSpacing: "0.2em"
      }}>
          &mdash; Friends of the Refuges Advocacy Team
        </p>
      </div>
    </div>;
}
export default function Conservation() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="4095" id="our-conservation-milestones" className="dark bg-slate-50 text-slate-900 py-24 md:py-36 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div ref={headRef} className="mb-20 md:mb-28">
          <div className="overflow-hidden" style={{
          transition: "clip-path 900ms cubic-bezier(0.4, 0, 0.2, 1), opacity 900ms ease",
          clipPath: headVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
          opacity: headVisible ? 1 : 0
        }}>
            <p className="font-default text-xs uppercase text-green-700 mb-10" style={{
            letterSpacing: "0.24em"
          }}>
              Our Process
            </p>
            <h2 className="font-default text-5xl md:text-7xl lg:text-[80px] leading-[1.05] text-slate-900" style={{
            fontWeight: 600
          }}>
              Our conservation
              <span className="block md:pl-20">milestones.</span>
            </h2>
            <p className="mt-10 font-default italic text-lg md:text-xl text-slate-700 max-w-xl leading-relaxed" style={{
            fontWeight: 400
          }}>
              Six deliberate steps to ensure the safety of our wildlife and sea bird refuges.
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200" aria-hidden="true" />

          <div className="flex flex-col gap-24 md:gap-32">
            {stages.slice(0, 3).map((stage, i) => <StageBlock key={stage.number} stage={stage} index={i} />)}

            <PullQuote />

            {stages.slice(3).map((stage, i) => <StageBlock key={stage.number} stage={stage} index={i + 3} />)}
          </div>
        </div>
      </div>
    </section>;
}